import { useEffect, useCallback } from 'react';
import FormStorageService from './form-storage-service';
export const useFormPersistence = ({ eventId, watch, setValue, getValues, formSteps }) => {
    // Load saved form data when component mounts
    useEffect(() => {
        if (!eventId || !formSteps.length)
            return;
        try {
            const savedValues = FormStorageService.getFieldValues(eventId);
            // Automatically restore saved values for each field
            Object.entries(savedValues).forEach(([fieldId, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    setValue(fieldId, value, { shouldValidate: false });
                }
            });
        }
        catch (error) {
            console.error('Error loading saved form data:', error);
        }
    }, [eventId, formSteps, setValue]);
    // Watch for form changes and save to storage
    const watchedValues = watch();
    useEffect(() => {
        if (!eventId || !watchedValues)
            return;
        // Debounce the save operation to avoid excessive storage writes
        const timeoutId = setTimeout(() => {
            try {
                Object.entries(watchedValues).forEach(([fieldId, value]) => {
                    // Only save if the value is not empty and not undefined
                    if (value !== undefined && value !== null && value !== '') {
                        FormStorageService.saveFormField(eventId, fieldId, value);
                    }
                });
            }
            catch (error) {
                console.error('Error saving form data:', error);
            }
        }, 500); // 500ms debounce
        return () => clearTimeout(timeoutId);
    }, [eventId, watchedValues]);
    // Function to clear form data for this event
    const clearFormData = useCallback(() => {
        if (eventId) {
            FormStorageService.clearFormData(eventId);
        }
    }, [eventId]);
    // Function to check if there's saved data for this event
    const hasSavedData = useCallback(() => {
        return eventId ? FormStorageService.hasFormData(eventId) : false;
    }, [eventId]);
    // Function to get the last updated timestamp
    const getLastUpdated = useCallback(() => {
        return eventId ? FormStorageService.getLastUpdated(eventId) : 0;
    }, [eventId]);
    // Function to restore form to last saved state
    const restoreFormData = useCallback(() => {
        if (!eventId)
            return;
        try {
            const savedValues = FormStorageService.getFieldValues(eventId);
            Object.entries(savedValues).forEach(([fieldId, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    setValue(fieldId, value, { shouldValidate: false });
                }
            });
        }
        catch (error) {
            console.error('Error restoring form data:', error);
        }
    }, [eventId, setValue]);
    return {
        clearFormData,
        hasSavedData,
        getLastUpdated,
        restoreFormData
    };
};
